import { useState } from "react";
import { Clock, LogOut, X, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { signOut } from "@/lib/auth-client";

interface PendingApprovalProps {
  tenantName: string;
  createdAt: Date;
  onCancel: () => void;
}

export function PendingApproval({
  tenantName,
  createdAt,
  onCancel,
}: PendingApprovalProps) {
  const navigate = useNavigate();
  const [isCancelling, setIsCancelling] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      await onCancel();
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      navigate("/login", { replace: true });
    } catch {
      setIsSigningOut(false);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("es", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  };

  return (
    <Card>
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 rounded-full bg-amber-500/10 p-3 w-fit">
          <Clock className="h-6 w-6 text-amber-500" />
        </div>
        <CardTitle>Solicitud pendiente</CardTitle>
        <CardDescription>
          Tu solicitud esta siendo revisada por un administrador
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-lg bg-muted p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Empresa</span>
            <span className="font-medium">{tenantName}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Enviada</span>
            <span className="font-medium">{formatDate(createdAt)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Estado</span>
            <span className="inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-600">
              Pendiente
            </span>
          </div>
        </div>

        <p className="text-sm text-muted-foreground text-center">
          Te notificaremos cuando tu solicitud sea aprobada. Puedes cerrar esta
          pagina y volver mas tarde.
        </p>

        <div className="flex flex-col gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isCancelling || isSigningOut}
          >
            {isCancelling ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <X className="mr-2 h-4 w-4" />
            )}
            Cancelar solicitud
          </Button>
          <Button
            variant="ghost"
            onClick={handleSignOut}
            disabled={isCancelling || isSigningOut}
          >
            {isSigningOut ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <LogOut className="mr-2 h-4 w-4" />
            )}
            Cerrar sesion
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
